import React from "react";
import PropTypes from "prop-types";
import { tabPath } from "./tabRouting";

const barStyle = {
  display: "flex",
  gap: 28,
  borderBottom: "1px solid #d2d3d4",
  marginBottom: 16,
};

const tabStyle = (active) => ({
  background: "none",
  border: "none",
  borderBottom: active ? "2px solid #533dd1" : "2px solid transparent",
  padding: "10px 2px",
  marginBottom: -1,
  fontSize: 14,
  color: active ? "#533dd1" : "#4d5357",
  fontWeight: active ? 600 : 400,
  cursor: "pointer",
  whiteSpace: "nowrap",
  textDecoration: "none",
  display: "inline-block",
});

// V1-COMPAT: styling for a `disabled` tab entry — inert, visibly non-interactive.
const disabledTabStyle = (active) => ({
  ...tabStyle(active),
  color: "#a7abaf",
  cursor: "not-allowed",
  opacity: 0.6,
});

// A plain left-click swaps tabs in place (SPA-smooth, no reload). Anything else — middle-click,
// cmd/ctrl/shift/alt-click — is left to the browser's native anchor behavior (e.g. open in a new
// tab), because the href is a real, working page.
function isPlainLeftClick(e) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

// The tab set comes from the server (Frontend::Concerns::RendersDirectory), which decides whether
// this page is the Source Directory or the Media Database. Keeping the list there means the tab bar,
// the sidebar and the heading all read from one place.
function TabBar({ tabs, activeTab, onTabChange, tabPathMap }) {
  const basePathname = window.location.pathname;

  return (
    <nav className="sd-tab-bar" style={barStyle}>
      {tabs.map(({ key, label, href, disabled }) => {
        // V1-COMPAT: an entry-level `href` is a real page (possibly outside this app's routing);
        // it renders as a plain navigating anchor with no click interception.
        const resolvedHref = href ?? tabPath(basePathname, key, tabPathMap);

        return (
          <a
            key={key}
            href={resolvedHref}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            style={disabled ? disabledTabStyle(activeTab === key) : tabStyle(activeTab === key)}
            onClick={(e) => {
              // V1-COMPAT: a disabled tab is inert — it never navigates and never swaps tabs.
              if (disabled) {
                e.preventDefault();
                return;
              }
              if (href) return;
              if (!isPlainLeftClick(e)) return;
              e.preventDefault();
              onTabChange(key);
            }}
          >
            {label}
          </a>
        );
      })}
    </nav>
  );
}

TabBar.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      // V1-COMPAT: optional plain-navigation href and inert/disabled state for a tab entry.
      href: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  // V1-COMPAT: optional V2 tab key -> host path map, forwarded to tabPath for href derivation.
  tabPathMap: PropTypes.objectOf(PropTypes.string),
};

export default TabBar;
